import type { DataGridColumnVisibilityState, DataGridFilterState, DataGridSortingState } from '@ibsys/kit';
import { useEffect, useRef } from 'react';
import {
  type DataGridColumnActions,
  type DataGridColumnSettings,
  useDataGridColumnsSettings,
} from '@/entities/settings';
import { isContentSearchColumnId } from './constants';

/**
 * Состояние колонок списка диалогов: общий каркас «состояние грида ↔ настройки пользователя»
 * из `@/entities/settings` со спецификой списка.
 *
 * Специфика: состав колонок известен только из данных (атрибуты, заключения и их блоки), поэтому
 * порядок по умолчанию не задан, а сохранённое состояние применяется лишь после их загрузки;
 * ширина колонок поиска по содержимому живёт до конца сеанса; видимость по умолчанию при первом
 * открытии пишется в настройки — вид таблицы должен быть описан целиком, иначе он не сравним
 * со снимком пресета, где видимость задана явно.
 *
 * @param defaultVisibility - видимость всех известных динамических колонок (из `useDialogColumnsData`)
 * @param isDataLoading - true, пока данные колонок ещё грузятся
 */
export const useDialogColumnsState = (
  settings: DataGridColumnSettings,
  actions: DataGridColumnActions,
  filters: DataGridFilterState,
  sorting: DataGridSortingState,
  defaultVisibility: DataGridColumnVisibilityState,
  isDataLoading: boolean,
) => {
  const { gridColumnProps, columnVisibility, isReady } = useDataGridColumnsSettings({
    settings,
    actions,
    filters,
    sorting,
    defaultVisibility,
    isPending: isDataLoading,
    isEphemeralColumn: isContentSearchColumnId,
  });

  const isDefaultVisibilityStoredRef = useRef(false);

  useEffect(() => {
    if (isDataLoading || settings.columnVisibility || isDefaultVisibilityStoredRef.current) return;
    isDefaultVisibilityStoredRef.current = true;
    actions.setColumnState({ columnVisibility: defaultVisibility });
  }, [isDataLoading, settings.columnVisibility, defaultVisibility, actions]);

  return { gridColumnProps, columnVisibility, isReady };
};
